import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './App.css';
import logo from './logo.png'; 
import factoryVideo from './factory-video.mp4'; 
import productImg1 from './product1.png'; 
import productImg2 from './product2.png'; 

function App() {
  const { t, i18n } = useTranslation();
  
  // 1. STABLE FALLBACK DETECTOR: If i18n is loading or undefined, it defaults to English ('en')
  const currentLang = i18n.language || 'en';
  
  // 2. DETECT RTL: True only if the language actively starts with Arabic or Kurdish codes
  const isRTL = currentLang.startsWith('ar') || currentLang.startsWith('ku');
  const isLTR = !isRTL; // Forces English / LTR configuration on fresh visits

  // 3. GLOBAL DOCUMENT BINDING: Dynamically update the HTML page's direction attribute
  React.useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = currentLang;
  }, [isRTL, currentLang]);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setIsDropdownOpen(false);
  };

  const whatsappNumber = "9647704677070"; 
  const encodedMessage = encodeURIComponent(t('whatsapp_msg'));
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;

  // 4. FIXED BULLETPROOF ALIGNMENT CLASS: Explicit check using our solid isLTR fallback flag
  const textAlignmentClass = isLTR ? 'text-left' : 'text-right';

  return (
    <div className="app-container">
      
     {/* NAVIGATION BAR WITH CLICK DROPDOWN CONTROLLER */}
      <nav className="navbar">
        <div className="nav-container">
           <div className="nav-brand">
            <img src={logo} alt="Al-Quwa Logo" className="nav-logo" />
          </div>
          {/* DYNAMIC CLICK ACCORDION DROPDOWN */}
          <div className="lang-dropdown-wrapper">
            <button 
              className="lang-dropdown-trigger" 
              onClick={() => setIsDropdownOpen(!isDropdownOpen)} 
              aria-label="Select Language"
            >
              <svg viewBox="0 0 24 24" className="globe-icon" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
                <path d="M2 12h20" />
              </svg>
              <span className="current-lang-label">
                {i18n.language && i18n.language.startsWith('ar') ? 'العربية' : i18n.language && i18n.language.startsWith('ku') ? 'کوردی' : 'English'}
              </span>
            </button>
            
            {isDropdownOpen && (
              <div className="lang-dropdown-options">
                <button onClick={() => changeLanguage('ar')} className={i18n.language === 'ar' ? 'selected' : ''}>العربية</button>
                <button onClick={() => changeLanguage('ku')} className={i18n.language === 'ku' ? 'selected' : ''}>کوردی (سۆرانی)</button>
                <button onClick={() => changeLanguage('en')} className={i18n.language === 'en' ? 'selected' : ''}>English</button>
              </div>
            )}
          </div>

         
          
          
          <ul className="nav-links">
             <li><a href="#about">{t('nav_about')}</a></li>
             <li><a href="#dimensions">{t('nav_dimensions')}</a></li>
             <li><a href="#products">{t('nav_products')}</a></li>
             <li><a href="#specs">{t('nav_specs')}</a></li>
             <li><a href="#contact">{t('nav_contact')}</a></li>
          </ul>

          <a href="#contact" className="nav-cta">{t('contact_cta')}</a>
        </div>
      </nav>

      {/* HERO SECTION */}
      <header className="hero">
        <div className="hero-overlay">
          <div className="hero-content">
            <span className="hero-badge">{t('badge')}</span>
            <h1 className="hero-title-ar">{t('title_ar')}</h1>
            <h2 className="hero-title-en">{t('title_en')}</h2>
            <div className="hero-btns">
              <a href={whatsappLink} target="_blank" rel="noreferrer" className="btn-primary">{t('whatsapp_btn')}</a>
              <a href="#products" className="btn-secondary">{t('explore_btn')}</a>
            </div>
          </div>
        </div>
      </header>

      {/* 1. ABOUT & VIDEO SECTION */}
      <section id="about" className="about-video-section">
        <div className="container-grid" style={{ direction: isLTR ? 'ltr' : 'rtl' }}>
          <div className="video-wrapper">
            <video className="factory-video" controls autoPlay loop muted playsInline>
              <source src={factoryVideo} type="video/mp4" />
            </video>
          </div>
          <div className={`text-wrapper ${textAlignmentClass}`}>
            <h2 className="section-heading">{t('about_heading')}</h2>
            <p className="section-paragraph">{t('about_p1')}</p>
            <p className="section-paragraph">{t('about_p2')}</p>
          </div>
        </div>
      </section>

      {/* 2. WHAT IS SANDWICH PANEL */}
      <section className={`info-section bg-light ${textAlignmentClass}`} style={{ direction: isLTR ? 'ltr' : 'rtl' }}>
        <div className="container-fluid">
          <h2 className="section-heading center-text">{t('what_is_heading')}</h2>
          <p className="section-paragraph max-width-700 m-auto">{t('what_is_p')}</p>
        </div>
      </section>

      {/* 3. DIMENSIONS TABLE */}
      <section id="dimensions" className={`table-section ${textAlignmentClass}`}>
        <div className="container-fluid">
          <h2 className="section-heading center-text">{t('dim_heading')}</h2>
          <div className="table-responsive">
            <table className="dimensions-table" dir={isLTR ? 'ltr' : 'rtl'}>
              <thead>
                <tr>
                  <th>{t('th_features')}</th>
                  <th>{t('th_roof')}</th>
                  <th>{t('th_wall')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>{t('feat_width')}</strong></td>
                  <td>1000 mm</td>
                  <td>1000 mm</td>
                </tr>
                <tr>
                  <td><strong>{t('feat_length')}</strong></td>
                  <td>{t('val_length')}</td>
                  <td>{t('val_length')}</td>
                </tr>
                <tr>
                  <td><strong>{t('feat_thickness')}</strong></td>
                  <td>50, 75, 100 mm</td>
                  <td>50, 75, 100 mm</td>
                </tr>
                <tr>
                  <td><strong>{t('feat_density')}</strong></td>
                  <td>{t('val_density')}</td>
                  <td>{t('val_density')}</td>
                </tr>
                <tr>
                  <td><strong>{t('feat_conductivity')}</strong></td>
                  <td>0.022 w/mk</td>
                  <td>0.022 w/mk</td>
                </tr>
                <tr>
                  <td><strong>{t('feat_sound')}</strong></td>
                  <td>25 dB</td>
                  <td>25 dB</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 4. APPLICATIONS SECTION */}
      <section className={`applications-section bg-light ${textAlignmentClass}`}>
        <div className="container-fluid">
          <h2 className="section-heading center-text">{t('apps_heading')}</h2>
          <p className="section-paragraph center-text">{t('apps_sub')}</p>
          <div className="apps-grid" dir={isLTR ? 'ltr' : 'rtl'}>
            <div className="app-item">{t('app_showrooms')}</div>
            <div className="app-item">{t('app_restaurants')}</div>
            <div className="app-item">{t('app_pools')}</div>
            <div className="app-item">{t('app_schools')}</div>
            <div className="app-item">{t('app_malls')}</div>
            <div className="app-item">{t('app_poultry')}</div>
            <div className="app-item">{t('app_gyms')}</div>
            <div className="app-item">{t('app_airports')}</div>
          </div>
        </div>
      </section>

      {/* 5. PRODUCTS IMAGES SECTION */}
      <section id="products" className="products-images-section">
        <div className="container-fluid">
          <h2 className="section-heading center-text">{t('prod_heading')}</h2>
          
          {isLTR ? (
            <div style={{ display: 'block', width: '100%', marginBottom: '35px' }}>
              <div style={{ maxWidth: '460px', width: '100%', margin: '0 auto 0 0' }}>
                {['Rigid Polyurethane (PUR) Sandwich Panel', 'Rigid Polyisocyanurate (PIR) Sandwich Panel', 'Sandwich Panel Ridges'].map((item, idx) => (
                  <div key={idx} style={{ fontSize: '16px', fontWeight: '500', color: '#333333', lineHeight: '2.2', position: 'relative', direction: 'ltr', textAlign: 'left', paddingLeft: '22px' }}>
                    <span style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', left: 0, color: '#0056b3', fontSize: '28px', lineHeight: 1 }}>•</span>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ display: 'block', width: '100%', marginBottom: '35px' }}>
              <div style={{ maxWidth: '460px', width: '100%', margin: '0 0 0 auto' }}>
                {[t('nav1_product'), t('nav2_product'), 'Sandwich Panel Ridges'].map((item, idx) => (
                  <div key={idx} style={{ fontSize: '16px', fontWeight: '500', color: '#333333', lineHeight: '2.2', position: 'relative', direction: 'rtl', textAlign: 'right', paddingRight: '22px' }}>
                    <span style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', right: 0, color: '#0056b3', fontSize: '28px', lineHeight: 1 }}>•</span>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="products-grid-layout">
            <div className="product-card-pure-img">
              <img src={productImg1} alt="PUR Product Diagram" className="p-pure-img" />
            </div>
            <div className="product-card-pure-img">
              <img src={productImg2} alt="PIR Product Diagram" className="p-pure-img" />
            </div>
          </div>
        </div>
      </section>

      {/* 6. SPECIFICATIONS SECTION */}
      <section id="specs" className={`specs-section bg-light ${textAlignmentClass}`}>
        <div className="container-fluid">
          <h2 className="section-heading center-text">{t('specs_heading')}</h2>
          <div className="specs-grid" dir={isLTR ? 'ltr' : 'rtl'}>
            <div className="spec-item">{t('spec_rain')}</div>
            <div className="spec-item">{t('spec_rust')}</div>
            <div className="spec-item">{t('spec_wind')}</div>
            <div className="spec-item">{t('spec_sun')}</div>
            <div className="spec-item">{t('spec_chemical')}</div>
            <div className="spec-item">{t('spec_fire')}</div>
            <div className="spec-item">{t('spec_heat')}</div>
            <div className="spec-item">{t('spec_sound')}</div>
            <div className="spec-item">{t('spec_quake')}</div>
            <div className="spec-item">{t('spec_insects')}</div>
            <div className="spec-item">{t('spec_aesthetic')}</div>
            <div className="spec-item">{t('spec_eco')}</div>
            <div className="spec-item">{t('spec_cost')}</div>
            <div className="spec-item">{t('spec_colors')}</div>
            <div className="spec-item">{t('spec_durability')}</div> 
            <div className="spec-item">{t('spec_recyclable')}</div> 
          </div>
        </div>
      </section>

      {/* BOTTOM VIDEOS GRID */}
      <section className="bottom-videos-section">
        <div className="container-fluid">
          <h2 className="section-heading center-text">{t('video_heading')}</h2>
          <div className="videos-three-column-grid">
            <div className="video-card-item">
              <div className="video-box-wrapper">
                <video autoPlay loop muted playsInline controls preload="metadata">
                  <source src={require('./video1.mp4')} type="video/mp4" />
                </video>
              </div>
            </div>
            <div className="video-card-item">
              <div className="video-box-wrapper">
                <video autoPlay loop muted playsInline controls preload="metadata">
                  <source src={require('./video2.mp4')} type="video/mp4" />
                </video>
              </div>
            </div>
            <div className="video-card-item">
              <div className="video-box-wrapper">
                <video autoPlay loop muted playsInline controls preload="metadata">
                  <source src={require('./video3.mp4')} type="video/mp4" />
                </video>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. FOOTER */}
      <footer id="contact" className="main-footer" dir={isLTR ? 'ltr' : 'rtl'}>
        <div className="footer-grid-container">
          <div className={`footer-block ${textAlignmentClass}`}>
            <h3>{t('f_address_title')}</h3>
            <p>{t('f_address_val')}</p>
          </div>
          <div className={`footer-block ${textAlignmentClass}`}>
            <h3>{t('f_phone')}</h3>
            <p dir="ltr"><a href="tel:+9647704677070" className="f-link">+964 770 467 7070</a></p>
            <p dir="ltr"><a href="tel:+9647508050574" className="f-link">+964 750 805 0574</a></p>
            <p dir="ltr"><a href="tel:+9647801727171" className="f-link">+964 780 172 7171</a></p>
          </div>
          <div className={`footer-block ${textAlignmentClass}`}>
            <h3>{t('f_email')}</h3>
            <p dir="ltr"><a href="mailto:info@alqwapanel.com" className="f-link">info@alqwapanel.com</a></p>
          </div>
          <div className={`footer-block social-block ${textAlignmentClass}`}>
            <h3>{t('f_follow')}</h3>
            <div className="social-icons-wrapper">
              <a href="https://www.facebook.com/powerpanel/" target="_blank" rel="noreferrer" className="social-icon fb">Facebook</a>
              <a href="https://instagram.com/al_qwa_panel/" target="_blank" rel="noreferrer" className="social-icon ig">Instagram</a>
            </div>
          </div>
        </div>
        <hr className="footer-divider" />
        <p className="copyright center-text">© {new Date().getFullYear()} {t('copyright')}</p>
      </footer>

      {/* FLOATING WHATSAPP */}
      <a href={whatsappLink} target="_blank" rel="noreferrer" className="floating-whatsapp" title="WhatsApp">
        <svg viewBox="0 0 32 32" className="whatsapp-icon">
          <path d="M16 0c-8.837 0-16 7.163-16 16 0 2.825.737 5.607 2.137 8.048l-2.137 7.952 8.127-2.132c2.395 1.305 5.12 1.992 7.873 1.992 8.837 0 16-7.163 16-16s-7.163-16-16-16zm0 29.333c-2.451 0-4.856-.633-6.984-1.832l-.5-.283-4.819 1.264 1.285-4.787-.312-.497c-1.319-2.099-2.022-4.542-2.022-7.199 0-7.352 5.981-13.333 13.333-13.333 7.352 0 13.333 5.981 13.333 13.333s-5.981 13.333-13.333 13.333zm7.304-10.04c-.4-.2-2.37-1.17-2.74-1.304-.37-.135-.64-.2-.91.2-.27.4-.1.336-1.01 1.373-.3.34-.61.37-1.01.17-1.637-.821-2.852-1.425-4.004-3.4-.3-.52.3-.48.86-1.6.09-.19.04-.35-.02-.45-.06-.1-.54-1.31-.74-1.79-.2-.47-.4-.4-.54-.4h-.46c-.16 0-.42.06-.64.3-.22.24-.84.82-.84 2 0 1.19.86 2.33.98 2.5.12.16 1.69 2.58 4.1 3.62.57.25 1.02.4 1.37.51.58.18 1.1.16 1.52.1.47-.07 1.45-.59 1.65-1.16.2-.57.2-1.06.14-1.16-.06-.1-.23-.17-.63-.37z" fill="#FFF"/>
        </svg>
      </a>

    </div>
  );
}

export default App;